import { pageIntroContent } from '@insify/css/classes.module.css';
import groupBy from 'lodash/groupBy';
import { useEffect, useRef, useState } from 'react';

import { RichContentWithTooltip } from '../../components/RichContent';
import { WizardLayoutFormWithCommonSection } from '../../components/WizardLayoutForm';
import { useOnScreen } from '../../hooks/useOnScreen';

import { ExclusionsOverview } from './components/ExclusionsOverview';

import type { ExclusionsOverviewProps } from './components/ExclusionsOverview';

export type Exclusion<ExclusionType extends string> = {
  id?: string;
  type: ExclusionType;
};

export type ExclusionSegmentSpecific<SegmentType extends string> = {
  id: string;
  coverage: SegmentType;
  exclusion: string;
  order?: number;
};

type SubmitParams<ExclusionType extends string, CoverageType extends string> = {
  [ExclusionToSubmit in ExclusionType]?: Exclusion<ExclusionToSubmit>;
} & {
  exclusionsSegmentSpecific: ExclusionSegmentSpecific<CoverageType>[];
};

type ExclusionsProps<ExclusionType extends string, CoverageType extends string> = ExclusionsOverviewProps<
  ExclusionType,
  CoverageType
> & {
  isSubmitting: boolean;
  eventHandlers: {
    onSubmit: (submitParams: SubmitParams<ExclusionType, CoverageType>) => void;
    onBack: () => void;
  };
};

/**
 * @param props.exclusions - Exclusions that can be of any type
 * @param props.exclusionsSegmentSpecific - Exclusions that are shown in a list form grouped by their coverage
 * @param props.translations - Will change based on the provided exclusions type / exclusionsSegmentSpecific coverage
 * @param props.icons - Will change based on the provided exclusions type / exclusionsSegmentSpecific coverage
 */
export const Exclusions = <ExclusionType extends string, CoverageType extends string>({
  eventHandlers,
  isSubmitting,
  ...props
}: ExclusionsProps<ExclusionType, CoverageType>) => {
  const ref = useRef<HTMLDivElement>(null);
  const scrolledToBottom = useOnScreen(ref);
  const [hasBeenScrolledToBottom, setHasBeenScrolledToBottom] = useState(false);

  useEffect(() => {
    if (scrolledToBottom) {
      setHasBeenScrolledToBottom(true);
    }
  }, [scrolledToBottom]);

  const exclusionsGroupedByType = groupBy(props.exclusions, 'type');

  const confirmExclusions = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (hasBeenScrolledToBottom) {
      eventHandlers.onSubmit({
        exclusionsSegmentSpecific: props.exclusionsSegmentSpecific,
        ...Object(exclusionsGroupedByType),
      });
    } else {
      ref.current?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <WizardLayoutFormWithCommonSection
      isSubmitting={isSubmitting}
      backCopy={props.translations.back}
      onBack={eventHandlers.onBack}
      forwardCopy={props.translations.submit}
      onSubmit={confirmExclusions}
    >
      <RichContentWithTooltip content={props.content} className={pageIntroContent} />
      <ExclusionsOverview {...props} />
      <div data-testid='bottom' ref={ref} />
    </WizardLayoutFormWithCommonSection>
  );
};
